import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Paper,
  Link,
  Avatar,
  Divider,
} from '@mui/material';
import {
  GitCommit,
  Plus,
  Trash2,
  ExternalLink,
  GitBranch,
  FilePlus,
  FileMinus,
  FileCode,
  Copy,
  Check,
} from 'lucide-react';
import dayjs from 'dayjs';
import { Task, LinkedCommit, CommitProvider } from '@/types';
import { AppSelect, AppMenuItem } from '@/shared/components/AppSelect';

interface TaskCommitsTabProps {
  task: Task;
  onAddCommit?: (commit: Omit<LinkedCommit, 'id' | 'linkedAt' | 'linkedBy'>) => Promise<void>;
  onRemoveCommit?: (commitId: string) => Promise<void>;
  isLoading?: boolean;
  canEdit?: boolean;
}

interface AddCommitModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (commit: Omit<LinkedCommit, 'id' | 'linkedAt' | 'linkedBy'>) => Promise<void>;
}

// Provider info
const getProviderInfo = (provider: CommitProvider) => {
  switch (provider) {
    case 'github':
      return { label: 'GitHub', color: '#24292e', bgColor: '#f6f8fa', icon: '🐙' };
    case 'gitlab':
      return { label: 'GitLab', color: '#fc6d26', bgColor: '#fff4f0', icon: '🦊' };
    case 'bitbucket':
      return { label: 'Bitbucket', color: '#0052cc', bgColor: '#deebff', icon: '🪣' };
    case 'manual':
    default:
      return { label: 'Manual', color: '#64748b', bgColor: '#f1f5f9', icon: '📝' };
  }
};

// Add Commit Modal
const AddCommitModal: React.FC<AddCommitModalProps> = ({ open, onClose, onAdd }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    sha: '',
    message: '',
    author: '',
    authorEmail: '',
    provider: 'manual' as CommitProvider,
    repositoryName: '',
    repositoryUrl: '',
    commitUrl: '',
    branch: '',
    additions: '',
    deletions: '',
    filesChanged: '',
  });

  const handleSubmit = async () => {
    if (!formData.sha.trim() || !formData.message.trim()) return;
    
    setIsSubmitting(true);
    try {
      await onAdd({
        sha: formData.sha.trim(),
        message: formData.message.trim(),
        author: formData.author.trim() || 'Unknown',
        authorEmail: formData.authorEmail.trim() || undefined,
        provider: formData.provider,
        repositoryName: formData.repositoryName.trim() || undefined,
        repositoryUrl: formData.repositoryUrl.trim() || undefined,
        commitUrl: formData.commitUrl.trim() || undefined,
        branch: formData.branch.trim() || undefined,
        timestamp: new Date().toISOString(),
        additions: formData.additions ? parseInt(formData.additions) : undefined,
        deletions: formData.deletions ? parseInt(formData.deletions) : undefined,
        filesChanged: formData.filesChanged ? parseInt(formData.filesChanged) : undefined,
      });
      setFormData({
        sha: '',
        message: '',
        author: '',
        authorEmail: '',
        provider: 'manual',
        repositoryName: '',
        repositoryUrl: '',
        commitUrl: '',
        branch: '',
        additions: '',
        deletions: '',
        filesChanged: '',
      });
      onClose();
    } catch (error) {
      console.error('Failed to add commit:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GitCommit size={20} />
          Link Commit
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Commit SHA"
            value={formData.sha}
            onChange={(e) => setFormData({ ...formData, sha: e.target.value })}
            placeholder="e.g., abc123def456"
            fullWidth
            required
            helperText="The commit hash (can be shortened)"
          />
          
          <TextField
            label="Commit Message"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            placeholder="e.g., Fix bug in authentication flow"
            fullWidth
            required
            multiline
            rows={2}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="Author"
              value={formData.author}
              onChange={(e) => setFormData({ ...formData, author: e.target.value })}
              placeholder="John Doe"
              fullWidth
            />
            <TextField
              label="Author Email"
              value={formData.authorEmail}
              onChange={(e) => setFormData({ ...formData, authorEmail: e.target.value })}
              placeholder="john@example.com"
              fullWidth
            />
          </Box>

          <AppSelect
            label="Provider"
            value={formData.provider}
            onChange={(e) => setFormData({ ...formData, provider: e.target.value as CommitProvider })}
          >
            <AppMenuItem value="manual">Manual Entry</AppMenuItem>
            <AppMenuItem value="github">GitHub</AppMenuItem>
            <AppMenuItem value="gitlab">GitLab</AppMenuItem>
            <AppMenuItem value="bitbucket">Bitbucket</AppMenuItem>
          </AppSelect>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="Repository Name"
              value={formData.repositoryName}
              onChange={(e) => setFormData({ ...formData, repositoryName: e.target.value })}
              placeholder="owner/repo"
              fullWidth
              size="small"
            />
            <TextField
              label="Branch"
              value={formData.branch}
              onChange={(e) => setFormData({ ...formData, branch: e.target.value })}
              placeholder="main"
              fullWidth
              size="small"
            />
          </Box>

          <TextField
            label="Commit URL"
            value={formData.commitUrl}
            onChange={(e) => setFormData({ ...formData, commitUrl: e.target.value })}
            placeholder="https://github.com/owner/repo/commit/..."
            fullWidth
            size="small"
          />

          <Divider sx={{ my: 1 }} />

          <Typography variant="subtitle2" color="text.secondary">
            Change Statistics (optional)
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
            <TextField
              label="Additions"
              value={formData.additions}
              onChange={(e) => setFormData({ ...formData, additions: e.target.value })}
              placeholder="0"
              type="number"
              size="small"
              InputProps={{
                startAdornment: <FilePlus size={14} style={{ marginRight: 4, color: '#22c55e' }} />,
              }}
            />
            <TextField
              label="Deletions"
              value={formData.deletions}
              onChange={(e) => setFormData({ ...formData, deletions: e.target.value })}
              placeholder="0"
              type="number"
              size="small"
              InputProps={{
                startAdornment: <FileMinus size={14} style={{ marginRight: 4, color: '#ef4444' }} />,
              }}
            />
            <TextField
              label="Files Changed"
              value={formData.filesChanged}
              onChange={(e) => setFormData({ ...formData, filesChanged: e.target.value })}
              placeholder="0"
              type="number"
              size="small"
              InputProps={{
                startAdornment: <FileCode size={14} style={{ marginRight: 4, color: '#4F46E5' }} />,
              }}
            />
          </Box>

          <Alert severity="info" sx={{ mt: 1 }}>
            <Typography variant="body2">
              <strong>Tip:</strong> Connect to GitHub in task integrations to automatically fetch commits.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!formData.sha.trim() || !formData.message.trim() || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : <Plus size={16} />}
        >
          Link Commit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * TaskCommitsTab
 * Display and manage commits linked to a task
 */
const TaskCommitsTab: React.FC<TaskCommitsTabProps> = ({
  task,
  onAddCommit,
  onRemoveCommit,
  isLoading,
  canEdit,
}) => {
  const editable = canEdit ?? true;
  const [showAddModal, setShowAddModal] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [copiedSha, setCopiedSha] = useState<string | null>(null);

  const commits = task.linkedCommits || [];

  const handleRemoveCommit = async (commitId: string) => {
    if (!onRemoveCommit || !editable) return;
    setRemovingId(commitId);
    try {
      await onRemoveCommit(commitId);
    } finally {
      setRemovingId(null);
    }
  };

  const handleCopySha = (sha: string) => {
    navigator.clipboard.writeText(sha);
    setCopiedSha(sha);
    setTimeout(() => setCopiedSha(null), 2000);
  };

  // Calculate total stats
  const totalStats = commits.reduce(
    (acc, commit) => ({
      additions: acc.additions + (commit.additions || 0),
      deletions: acc.deletions + (commit.deletions || 0),
      filesChanged: acc.filesChanged + (commit.filesChanged || 0),
    }),
    { additions: 0, deletions: 0, filesChanged: 0 }
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="subtitle2" color="text.secondary">
            {commits.length} commit{commits.length !== 1 ? 's' : ''}
          </Typography>
          {commits.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                icon={<FilePlus size={12} />}
                label={`+${totalStats.additions}`}
                size="small"
                sx={{ bgcolor: '#dcfce7', color: '#16a34a', '& .MuiChip-icon': { color: '#16a34a' } }}
              />
              <Chip
                icon={<FileMinus size={12} />}
                label={`-${totalStats.deletions}`}
                size="small"
                sx={{ bgcolor: '#fee2e2', color: '#dc2626', '& .MuiChip-icon': { color: '#dc2626' } }}
              />
            </Box>
          )}
        </Box>
        {editable && onAddCommit && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Plus size={16} />}
            onClick={() => setShowAddModal(true)}
          >
            Link Commit
          </Button>
        )}
      </Box>

      {task.integrations?.github?.enabled && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Connected to GitHub: {task.integrations.github.repositoryOwner}/{task.integrations.github.repositoryName}
            {task.integrations.github.defaultBranch && ` (${task.integrations.github.defaultBranch})`}
          </Typography>
        </Alert>
      )}

      {/* Empty State */}
      {commits.length === 0 ? (
        <Paper
          sx={{
            py: 6,
            px: 3,
            textAlign: 'center',
            bgcolor: 'grey.50',
            border: '1px dashed',
            borderColor: 'grey.300',
          }}
        >
          <GitCommit size={48} style={{ color: '#94a3b8', marginBottom: 16 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Commits Linked
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Link commits from GitHub, GitLab, or add them manually
          </Typography>
          {editable && onAddCommit && (
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={() => setShowAddModal(true)}
            >
              Link First Commit
            </Button>
          )}
        </Paper>
      ) : (
        <List disablePadding>
          {commits.map((commit, index) => {
            const providerInfo = getProviderInfo(commit.provider);
            const isRemoving = removingId === commit.id;
            const shortSha = commit.sha.substring(0, 7);

            return (
              <ListItem
                key={commit.id}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: 'grey.50',
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: providerInfo.bgColor,
                      color: providerInfo.color,
                      fontSize: '0.9rem',
                    }}
                    src={commit.authorAvatar}
                  >
                    {commit.author?.charAt(0).toUpperCase() || 'U'}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>
                        {commit.message.split('\n')[0]}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Tooltip title={copiedSha === commit.sha ? 'Copied!' : 'Click to copy'}>
                          <Chip
                            icon={copiedSha === commit.sha ? <Check size={12} /> : <Copy size={12} />}
                            label={shortSha}
                            size="small"
                            onClick={() => handleCopySha(commit.sha)}
                            sx={{
                              height: 22,
                              fontSize: '0.75rem',
                              fontFamily: 'monospace',
                              cursor: 'pointer',
                              bgcolor: copiedSha === commit.sha ? '#dcfce7' : '#f1f5f9',
                              '&:hover': { bgcolor: '#e2e8f0' },
                            }}
                          />
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary">
                          by {commit.author}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          • {dayjs(commit.timestamp).fromNow()}
                        </Typography>
                        {commit.branch && (
                          <Chip
                            icon={<GitBranch size={12} />}
                            label={commit.branch}
                            size="small"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        )}
                        <Chip
                          label={providerInfo.label}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            bgcolor: providerInfo.bgColor,
                            color: providerInfo.color,
                          }}
                        />
                      </Box>
                      {(commit.additions !== undefined || commit.deletions !== undefined) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          {commit.additions !== undefined && (
                            <Typography variant="caption" sx={{ color: '#16a34a' }}>
                              +{commit.additions}
                            </Typography>
                          )}
                          {commit.deletions !== undefined && (
                            <Typography variant="caption" sx={{ color: '#dc2626' }}>
                              -{commit.deletions}
                            </Typography>
                          )}
                          {commit.filesChanged !== undefined && (
                            <Typography variant="caption" color="text.secondary">
                              ({commit.filesChanged} files)
                            </Typography>
                          )}
                          {commit.repositoryName && (
                            <Typography variant="caption" color="text.secondary">
                              in {commit.repositoryName}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  {commit.commitUrl && (
                    <Tooltip title="View commit">
                      <IconButton
                        size="small"
                        onClick={() => window.open(commit.commitUrl, '_blank')}
                        sx={{ mr: 0.5 }}
                      >
                        <ExternalLink size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {editable && onRemoveCommit && (
                    <Tooltip title="Remove link">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveCommit(commit.id)}
                        disabled={isRemoving}
                      >
                        {isRemoving ? <CircularProgress size={16} /> : <Trash2 size={16} />}
                      </IconButton>
                    </Tooltip>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      )}

      {/* Add Commit Modal */}
      {editable && onAddCommit && (
        <AddCommitModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAdd={onAddCommit}
        />
      )}
    </Box>
  );
};

export default TaskCommitsTab;
